import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { btnSecondaryCls } from "components/tw/classes";
import StatusChip, { ChipTone } from "components/tw/status-chip";
import { QueueCell, QueueRow, QueueTable } from "features/cabinet-queues/queue-table";
import { FuelMonthRow, FuelMonthStatus } from "services/cabinet.service";

/**
 * How a month row reads. It is looked up by the `status` the server gives the month.
 * `chipKey` and `tone` make the label in the «Būsena» column. `button` is the step
 * the person takes, if there is one: `form` opens the form for a new report, `report`
 * opens the page of the existing one. A status without a button only informs.
 * `monthOpensReport` makes the month itself a link to the report; it is for the one
 * finished status that has no button.
 */
const MONTH_STATUSES: Record<
  FuelMonthStatus,
  {
    chipKey: string;
    tone: ChipTone;
    button?: { labelKey: string; opens: "form" | "report" };
    monthOpensReport?: boolean;
  }
> = {
  missing: {
    chipKey: "cabinet_fuel_status_missing",
    tone: "red",
    button: { labelKey: "cabinet_action_fill", opens: "form" },
  },
  current: { chipKey: "cabinet_fuel_status_current", tone: "grey" },
  draft: {
    chipKey: "cabinet_fuel_status_draft",
    tone: "grey",
    button: { labelKey: "cabinet_action_continue", opens: "report" },
  },
  to_sign: {
    chipKey: "cabinet_fuel_status_to_sign",
    tone: "amber",
    button: { labelKey: "tasks_action_sign", opens: "report" },
  },
  waiting_manager: { chipKey: "cabinet_fuel_status_waiting_manager", tone: "amber" },
  rejected: {
    chipKey: "cabinet_fuel_status_rejected",
    tone: "red",
    button: { labelKey: "cabinet_action_fix", opens: "report" },
  },
  approved: { chipKey: "cabinet_fuel_status_approved", tone: "green", monthOpensReport: true },
  annulled: { chipKey: "cabinet_fuel_status_annulled", tone: "grey" },
};

interface FuelMonthLineProps {
  row: FuelMonthRow;
  /** The address of the page of the report this row has. */
  reportPath: (row: FuelMonthRow) => string;
  /** Opens the form for a new report of this row's month and vehicle. */
  onFill: (row: FuelMonthRow) => void;
}

interface FuelMonthTableProps extends Omit<FuelMonthLineProps, "row"> {
  rows: FuelMonthRow[];
}

/** The months of the person's vehicles, in the order the server sent them (newest first). */
export default function FuelMonthTable({ rows, reportPath, onFill }: FuelMonthTableProps) {
  const { t } = useTranslation();
  const columns = [
    { heading: t("cabinet_fuel_col_month"), width: "7rem" },
    { heading: t("cabinet_fuel_col_vehicle"), width: "minmax(0,1fr)" },
    { heading: t("cabinet_fuel_col_status"), width: "11rem" },
    { heading: t("cabinet_fuel_col_action"), width: "7rem" },
  ];

  return (
    <QueueTable label={t("cabinet_fuel_table_label")} columns={columns}>
      {rows.map((row) => (
        <FuelMonthLine key={`${row.period}-${row.asset.id}`} row={row} reportPath={reportPath} onFill={onFill} />
      ))}
    </QueueTable>
  );
}

function FuelMonthLine({ row, reportPath, onFill }: FuelMonthLineProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { chipKey, tone, button, monthOpensReport } = MONTH_STATUSES[row.status];

  return (
    <QueueRow>
      <QueueCell isTitle>
        {monthOpensReport ? (
          <Link to={reportPath(row)} className="text-indigo-600 hover:underline">
            {row.period}
          </Link>
        ) : (
          row.period
        )}
      </QueueCell>
      <QueueCell>
        {row.asset.name}
        {row.asset.plate && <span className="ml-2 text-gray-500">{row.asset.plate}</span>}
      </QueueCell>
      <QueueCell>
        <StatusChip tone={tone}>{t(chipKey)}</StatusChip>
      </QueueCell>
      <QueueCell className="ml-auto md:ml-0 md:text-right">
        {button && (
          <button
            type="button"
            className={btnSecondaryCls}
            onClick={() => (button.opens === "form" ? onFill(row) : navigate(reportPath(row)))}
          >
            {t(button.labelKey)}
          </button>
        )}
      </QueueCell>
    </QueueRow>
  );
}
